/**
 * `/v1/ops/guaranteed-guard-config` — 保障配置 v2 (guard-v2).
 * List + PUT of one row's `policy` patch. The table is independent of
 * guaranteed-service entries; writes do not PATCH `no_response_cutoff_ms`.
 */
import {
  parseGuardPolicyRead,
  type GuaranteedServicePropagationResponse,
  type RoutingCacheConvergence,
  type StoredGuardPolicy,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import type { GuardPutBody } from '../pages/guaranteed/guard/guardModel.ts';
import { opsApiRequest } from './client.ts';

export const GUARANTEED_GUARD_LIST_KEY = ['guaranteed-guard-config'] as const;

export interface GuardWriteGate {
  readonly open: boolean;
  readonly reason: string | null;
  readonly checked_at: string | null;
}

export interface GuardConfigRow {
  readonly id: string;
  readonly scope: 'sku' | 'org_sku';
  readonly org_id: string | null;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly policy: StoredGuardPolicy;
  readonly note: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListGuardConfigResponse {
  readonly sku: readonly GuardConfigRow[];
  readonly org_sku: readonly GuardConfigRow[];
  readonly write_gate: GuardWriteGate;
}

export interface PutGuardConfigResponse {
  readonly item: GuardConfigRow;
  readonly cache: RoutingCacheConvergence | null;
}

const OPEN_GATE: GuardWriteGate = { open: true, reason: null, checked_at: null };

function asIso(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function objectRecord(value: unknown): Readonly<Record<string, unknown>> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  return Object.fromEntries(Object.entries(value));
}

function mapRow(raw: unknown): GuardConfigRow | null {
  const row = objectRecord(raw);
  if (row === null) return null;
  if (row.scope !== 'sku' && row.scope !== 'org_sku') return null;
  if (typeof row.id !== 'string' || typeof row.sku_id !== 'string') return null;
  return {
    id: row.id,
    scope: row.scope,
    org_id: typeof row.org_id === 'string' ? row.org_id : null,
    sku_id: row.sku_id,
    sku_name: typeof row.sku_name === 'string' ? row.sku_name : '',
    policy: parseGuardPolicyRead(row.policy),
    note: typeof row.note === 'string' ? row.note : null,
    created_by: typeof row.created_by === 'string' ? row.created_by : null,
    created_at: asIso(row.created_at),
    updated_at: asIso(row.updated_at),
  };
}

function mapCache(raw: unknown): RoutingCacheConvergence | null {
  const rec = objectRecord(raw);
  if (rec === null) return null;
  if (rec.convergence !== 'immediate' && rec.convergence !== 'degraded') return null;
  if (typeof rec.effective_after !== 'string') return null;
  if (!Array.isArray(rec.scopes) || !rec.scopes.every((item) => typeof item === 'string')) return null;
  return {
    revision: typeof rec.revision === 'string' ? rec.revision : null,
    convergence: rec.convergence,
    effective_after: rec.effective_after,
    scopes: rec.scopes,
  };
}

function mapList(raw: unknown): ListGuardConfigResponse {
  const body = objectRecord(raw);
  if (body === null) return { sku: [], org_sku: [], write_gate: OPEN_GATE };
  const sku = Array.isArray(body.sku) ? body.sku.map(mapRow).filter((row): row is GuardConfigRow => row !== null) : [];
  const orgSku = Array.isArray(body.org_sku) ? body.org_sku.map(mapRow).filter((row): row is GuardConfigRow => row !== null) : [];
  const gateRaw = objectRecord(body.write_gate);
  const write_gate = gateRaw === null
    ? OPEN_GATE
    : {
      open: gateRaw.open === true,
      reason: typeof gateRaw.reason === 'string' ? gateRaw.reason : null,
      checked_at: typeof gateRaw.checked_at === 'string' ? gateRaw.checked_at : null,
    };
  return { sku, org_sku: orgSku, write_gate };
}

export function useGuaranteedGuardConfigList(): UseQueryResult<ListGuardConfigResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: GUARANTEED_GUARD_LIST_KEY,
    queryFn: async () => mapList(await opsApiRequest<unknown>(
      '/v1/ops/guaranteed-guard-config',
      {},
      fetchImpl,
    )),
    staleTime: 30_000,
  });
}

export function usePutGuaranteedGuardConfig(): UseMutationResult<
  PutGuardConfigResponse,
  Error,
  GuardPutBody
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request) => {
      const raw = await opsApiRequest<unknown>(
        '/v1/ops/guaranteed-guard-config',
        { method: 'PUT', body: request },
        fetchImpl,
      );
      const payload = objectRecord(raw);
      const item = payload === null ? null : mapRow(payload.item);
      if (item === null) throw new Error('guaranteed guard config write returned no item');
      return { item, cache: payload === null ? null : mapCache(payload.cache) };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GUARANTEED_GUARD_LIST_KEY });
    },
  });
}

export async function fetchGuardConfigPropagation(
  id: string,
  expectedUpdatedAt: string | null,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GuaranteedServicePropagationResponse> {
  const qs = expectedUpdatedAt === null ? '' : `?updated_at=${encodeURIComponent(expectedUpdatedAt)}`;
  return opsApiRequest<GuaranteedServicePropagationResponse>(
    `/v1/ops/guaranteed-guard-config/${encodeURIComponent(id)}/propagation${qs}`,
    { signal },
    fetchImpl,
  );
}

export function skuGuardOf(
  list: ListGuardConfigResponse | undefined,
  skuId: string,
): GuardConfigRow | undefined {
  return list?.sku.find((row) => row.sku_id === skuId);
}

export function orgGuardsOf(
  list: ListGuardConfigResponse | undefined,
  skuId: string,
): readonly GuardConfigRow[] {
  return (list?.org_sku ?? []).filter((row) => row.sku_id === skuId);
}

export function orgGuardOf(
  list: ListGuardConfigResponse | undefined,
  skuId: string,
  orgId: string,
): GuardConfigRow | undefined {
  return list?.org_sku.find((row) => row.sku_id === skuId && row.org_id === orgId);
}
